/**
 * Self-hosted brand font registry (theming.md — "must resolve to a self-hosted/system stack;
 * never a font-CDN URL, CSP blocks it"). The crawler can DETECT a Google-ish font name from a
 * town's site, but the CSP (`font-src 'self' data:`) blocks any request to fonts.googleapis.com —
 * so historically a crawled font just silently fell back to the browser default. Fixed by
 * bundling a curated set of common gov/municipal-site fonts via Fontsource (OFL-licensed woff2 +
 * @font-face, shipped from OUR bundle/origin — zero requests to Google, gov-privacy-safe).
 *
 * Design:
 *  - REGISTRY maps a canonical family name -> { load(), stack, category }. `load()` is a dynamic
 *    `import()` of ONLY that font's Fontsource CSS, so the other ~19 fonts never touch a resident's
 *    bundle — only the town's ACTIVE font is fetched, and only once (the module cache dedupes
 *    repeat calls for the same family within a session).
 *  - `loadBrandFont(raw)` normalizes whatever's saved on the tenant (`fonts` may be a bare family,
 *    a CSS stack like "Poppins, sans-serif", or free text from the old text field), matches it
 *    case/punctuation-insensitively against the registry, kicks off the dynamic import (best-effort
 *    — a network hiccup still resolves to a usable stack, it just won't have the webfont), and
 *    returns the CSS font-family stack to apply. An unmatched family NEVER results in a broken
 *    unloaded webfont reference — it maps to a close system stack (serif-ish name -> system serif
 *    stack, else system sans) so text always renders in something.
 *  - `font-display: swap` ships inside every Fontsource CSS file already, so there's never
 *    invisible text while the woff2 downloads.
 *  - `useBrandFontStacks` (bottom of this file) is the React hook the ThemeProvider (staff :root)
 *    and ResidentThemeRoot (resident scoped vars) both use to drive `--td-font-display`/`-body`.
 */
import { useEffect, useState } from "react";

export type FontCategory = "sans" | "serif" | "display";

interface FontEntry {
  /** Canonical display name shown in the admin picker. */
  label: string;
  category: FontCategory;
  /** Dynamic import of this font's Fontsource CSS — only fetched when this family is active. */
  load: () => Promise<unknown>;
  /** CSS font-family stack applied once loaded (matches the family name(s) the CSS declares). */
  stack: string;
}

const SYSTEM_SANS =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const SYSTEM_SERIF = 'Georgia, Cambria, "Times New Roman", Times, serif';

/**
 * Curated self-hosted set: ~20 fonts common on US municipal/gov sites, mixing sans/serif/display.
 * Public Sans (the US federal typeface, designed for USWDS) is included deliberately. Weights are
 * limited to 400/600/700 (or the nearest the family ships) — enough for body + emphasis + headings
 * without bundling every weight. Variable-font packages (`@fontsource-variable/*`) import a single
 * `wght.css` covering the full weight range in one file; static-only families (Lato, Poppins,
 * PT Serif — no variable build published) import the specific weight files we need.
 */
const REGISTRY: Record<string, FontEntry> = {
  "public sans": {
    label: "Public Sans",
    category: "sans",
    load: () => import("@fontsource-variable/public-sans"),
    stack: `"Public Sans Variable", ${SYSTEM_SANS}`,
  },
  roboto: {
    label: "Roboto",
    category: "sans",
    load: () => import("@fontsource-variable/roboto"),
    stack: `"Roboto Variable", ${SYSTEM_SANS}`,
  },
  "open sans": {
    label: "Open Sans",
    category: "sans",
    load: () => import("@fontsource-variable/open-sans"),
    stack: `"Open Sans Variable", ${SYSTEM_SANS}`,
  },
  lato: {
    label: "Lato",
    category: "sans",
    load: () =>
      Promise.all([
        import("@fontsource/lato/400.css"),
        import("@fontsource/lato/700.css"),
      ]),
    stack: `Lato, ${SYSTEM_SANS}`,
  },
  montserrat: {
    label: "Montserrat",
    category: "sans",
    load: () => import("@fontsource-variable/montserrat"),
    stack: `"Montserrat Variable", ${SYSTEM_SANS}`,
  },
  poppins: {
    label: "Poppins",
    category: "sans",
    load: () =>
      Promise.all([
        import("@fontsource/poppins/400.css"),
        import("@fontsource/poppins/600.css"),
        import("@fontsource/poppins/700.css"),
      ]),
    stack: `Poppins, ${SYSTEM_SANS}`,
  },
  inter: {
    label: "Inter",
    category: "sans",
    load: () => import("@fontsource-variable/inter"),
    stack: `"Inter Variable", ${SYSTEM_SANS}`,
  },
  "source sans 3": {
    label: "Source Sans 3",
    category: "sans",
    load: () => import("@fontsource-variable/source-sans-3"),
    stack: `"Source Sans 3 Variable", ${SYSTEM_SANS}`,
  },
  "source sans pro": {
    // Older Google Fonts name for the same family — alias so crawled legacy sites still match.
    label: "Source Sans 3",
    category: "sans",
    load: () => import("@fontsource-variable/source-sans-3"),
    stack: `"Source Sans 3 Variable", ${SYSTEM_SANS}`,
  },
  raleway: {
    label: "Raleway",
    category: "sans",
    load: () => import("@fontsource-variable/raleway"),
    stack: `"Raleway Variable", ${SYSTEM_SANS}`,
  },
  "nunito sans": {
    label: "Nunito Sans",
    category: "sans",
    load: () => import("@fontsource-variable/nunito-sans"),
    stack: `"Nunito Sans Variable", ${SYSTEM_SANS}`,
  },
  "work sans": {
    label: "Work Sans",
    category: "sans",
    load: () => import("@fontsource-variable/work-sans"),
    stack: `"Work Sans Variable", ${SYSTEM_SANS}`,
  },
  rubik: {
    label: "Rubik",
    category: "sans",
    load: () => import("@fontsource-variable/rubik"),
    stack: `"Rubik Variable", ${SYSTEM_SANS}`,
  },
  "dm sans": {
    label: "DM Sans",
    category: "sans",
    load: () => import("@fontsource-variable/dm-sans"),
    stack: `"DM Sans Variable", ${SYSTEM_SANS}`,
  },
  "libre franklin": {
    label: "Libre Franklin",
    category: "sans",
    load: () => import("@fontsource-variable/libre-franklin"),
    stack: `"Libre Franklin Variable", ${SYSTEM_SANS}`,
  },
  "noto sans": {
    label: "Noto Sans",
    category: "sans",
    load: () => import("@fontsource-variable/noto-sans"),
    stack: `"Noto Sans Variable", ${SYSTEM_SANS}`,
  },
  merriweather: {
    label: "Merriweather",
    category: "serif",
    load: () => import("@fontsource-variable/merriweather"),
    stack: `"Merriweather Variable", ${SYSTEM_SERIF}`,
  },
  lora: {
    label: "Lora",
    category: "serif",
    load: () => import("@fontsource-variable/lora"),
    stack: `"Lora Variable", ${SYSTEM_SERIF}`,
  },
  "playfair display": {
    label: "Playfair Display",
    category: "serif",
    load: () => import("@fontsource-variable/playfair-display"),
    stack: `"Playfair Display Variable", ${SYSTEM_SERIF}`,
  },
  "pt serif": {
    label: "PT Serif",
    category: "serif",
    load: () =>
      Promise.all([
        import("@fontsource/pt-serif/400.css"),
        import("@fontsource/pt-serif/700.css"),
      ]),
    stack: `"PT Serif", ${SYSTEM_SERIF}`,
  },
  "source serif 4": {
    label: "Source Serif 4",
    category: "serif",
    load: () => import("@fontsource-variable/source-serif-4"),
    stack: `"Source Serif 4 Variable", ${SYSTEM_SERIF}`,
  },
  oswald: {
    label: "Oswald",
    category: "display",
    load: () => import("@fontsource-variable/oswald"),
    stack: `"Oswald Variable", ${SYSTEM_SANS}`,
  },
};

/** Ordered for the admin picker (grouped display; insertion order = registry declaration order). */
export const SUPPORTED_FONTS: { key: string; label: string; category: FontCategory }[] =
  Object.entries(REGISTRY)
    // Drop the legacy-name alias from the picker list — it round-trips detection, not selection.
    .filter(([key]) => key !== "source sans pro")
    .map(([key, entry]) => ({ key, label: entry.label, category: entry.category }));

/**
 * Explicit "System" picker options (as opposed to the SYSTEM_SANS/SERIF stacks used only as an
 * internal fallback) — a clerk can deliberately choose "System sans-serif"/"System serif" instead
 * of a self-hosted brand font. Selecting one saves a plain, recognizable value (not a registry key)
 * so it round-trips through the free-text `fonts` field unambiguously.
 */
export const SYSTEM_FONT_OPTIONS = [
  { key: "system-sans", label: "System sans-serif", value: "system-ui, sans-serif" },
  { key: "system-serif", label: "System serif", value: "Georgia, serif" },
] as const;

/** Per-family in-flight/completed load promise, so repeat calls in one session don't re-import. */
const loadCache = new Map<string, Promise<unknown>>();

/**
 * Normalize a saved/detected font value down to a bare family name for registry lookup:
 * "Poppins, sans-serif" -> "poppins"; "'Source Sans 3'" -> "source sans 3"; trims + lowercases.
 * Takes only the FIRST comma-separated entry (the primary family) — the rest of a CSS stack (if
 * any) is a fallback list we replace with our own anyway.
 */
function primaryFamily(raw: string): string {
  const first = raw.split(",")[0] ?? "";
  return first.trim().replace(/^["']|["']$/g, "").toLowerCase();
}

/** Serif-leaning name heuristics, used only when the family isn't in our registry. */
function looksSerif(name: string): boolean {
  return /serif|georgia|times|garamond|palatino|book antiqua|cambria|iowan|minion|caslon/i.test(
    name,
  );
}

export interface LoadedFont {
  /** CSS font-family stack to assign to the font var. Always usable — never a dangling webfont ref. */
  stack: string;
  /** True when the family matched a self-hosted registry entry (vs. a system fallback). */
  selfHosted: boolean;
  /** The registry key that matched, if any — lets callers show "using X" copy. */
  matchedKey: string | null;
}

/**
 * Resolve a tenant's saved/crawled `fonts` value to a font-family stack, dynamically importing the
 * self-hosted webfont CSS if it's in our curated set. Never throws and never returns a stack that
 * references an unloaded webfont — an unmatched or falsy input resolves to a system stack (serif
 * heuristic if the name looks serif-ish, otherwise the sans default) so text always renders.
 */
export async function loadBrandFont(raw: string | null | undefined): Promise<LoadedFont> {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return { stack: SYSTEM_SANS, selfHosted: false, matchedKey: null };

  const key = primaryFamily(trimmed);
  const entry = REGISTRY[key];
  if (!entry) {
    return {
      stack: looksSerif(trimmed) ? SYSTEM_SERIF : SYSTEM_SANS,
      selfHosted: false,
      matchedKey: null,
    };
  }

  try {
    let pending = loadCache.get(key);
    if (!pending) {
      pending = entry.load();
      loadCache.set(key, pending);
    }
    await pending;
    return { stack: entry.stack, selfHosted: true, matchedKey: key };
  } catch {
    // The dynamic import failed (rare — e.g. a corrupted build asset). Don't leave the caller with
    // a stack naming a font whose @font-face never registered; drop back to a system stack instead.
    loadCache.delete(key);
    return {
      stack: entry.category === "serif" ? SYSTEM_SERIF : SYSTEM_SANS,
      selfHosted: false,
      matchedKey: null,
    };
  }
}

/**
 * Synchronous best-effort lookup used by the admin picker to PRESELECT a crawler-detected font
 * without awaiting the actual webfont download (the picker just needs to know "do we support this
 * family and what do we call it", not have the font loaded yet — Save still routes through
 * loadBrandFont like everything else).
 */
export function matchBrandFont(raw: string | null | undefined): { key: string; label: string } | null {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return null;
  const key = primaryFamily(trimmed);
  const entry = REGISTRY[key];
  return entry ? { key, label: entry.label } : null;
}

export function fontStackForKey(key: string): string | null {
  const entry = REGISTRY[key];
  return entry ? entry.stack : null;
}

export interface BrandFontStacks {
  display: string;
  body: string;
}

const INITIAL_STACKS: BrandFontStacks = { display: SYSTEM_SANS, body: SYSTEM_SANS };

/**
 * React hook: resolves a tenant's `fonts.display`/`fonts.body` to renderable CSS stacks, dynamically
 * loading each self-hosted webfont on demand. Returns a SYSTEM stack immediately (so callers always
 * have something to paint with on the very first render) and re-renders once each stack's
 * `loadBrandFont` resolves. Re-resolves whenever the display/body family strings change (e.g. the
 * clerk picks a different font, or the embed-config finishes loading).
 */
export function useBrandFontStacks(
  fonts: { display?: string; body?: string } | undefined,
): BrandFontStacks {
  const display = fonts?.display ?? "";
  const body = fonts?.body ?? "";
  const [stacks, setStacks] = useState<BrandFontStacks>(INITIAL_STACKS);

  useEffect(() => {
    let live = true;
    Promise.all([loadBrandFont(display), loadBrandFont(body)]).then(([d, b]) => {
      if (!live) return;
      setStacks({ display: d.stack, body: b.stack });
    });
    return () => {
      live = false;
    };
  }, [display, body]);

  return stacks;
}
